import React from 'react'
import moment from "moment"
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { Card, ErrorBoundary } from "components"
import { useGet, usePersistForm } from "hooks"
import { FormProvider, useForm } from "react-hook-form"
import { IPaginatedEntityGST } from "services/core/genericEntityGST"
import { IGetOcorrenciasByFilterParams, IOcorrenciasByFilterItens } from "services/GST/WorkAccidentOccurrence"
import { FORMAT, FUNCTIONS, SPACING } from "utils"

import AccidentTable from './components/AccidentTable'
import SearchForm from './components/SearchForm'
import { IForm } from "./types"

function validateDate(value: unknown) {
  const isInvalidDate = false
  const isValidDate = true

  if (!value) {
    return isValidDate
  }

  if (moment(value).isValid()) {
    return isValidDate
  }

  return isInvalidDate
}

const schema = yup.object().shape({
  nuCompetenciaInicio: yup.string()
    .test('date-ini', 'Digite uma data válida', validateDate)
    .test('date-periodo', 'Período final não pode ser menor que período inicial', function (nuCompetenciaInicio: unknown) {
      const { nuCompetenciaFim } = this?.parent

      if (nuCompetenciaFim && moment(nuCompetenciaFim).isBefore(nuCompetenciaInicio)) {
        return false
      }

      return true
    })
    .nullable(),
  nuCompetenciaFim: yup.string()
    .test('date-end', 'Digite uma data válida', validateDate)
    .test('date-periodo', 'Período inicial não pode ser maior que período final', function (nuCompetenciaFim: unknown) {
      const { nuCompetenciaInicio } = this?.parent

      if (nuCompetenciaInicio && moment(nuCompetenciaInicio).isAfter(nuCompetenciaFim)) {
        return false
      }

      return true
    })
    .nullable()
})

const Search: React.FC = () => {

  const methods = useForm<IForm>({
    defaultValues: {
      cdTipoFornecedor: 0,
      idCentroPlanta: 0,
      nuCompetenciaFim: null,
      nuCompetenciaInicio: null,
      txCpfCnpjPassaporte: '',
      txEmpresa: '',
      txFornecedor: '',
    },
    resolver: yupResolver(schema)
  })

  usePersistForm('register-work-accident0occurence-form', {
    setValue: methods.setValue,
    watch: methods.watch
  })

  const {
    data: tableData,
    refresh: refreshTable,
    handleChangePage,
    handleChangeRowsPerPage,
    isFirstGet,
    page,
    rowsPerPage
  } = useGet<IGetOcorrenciasByFilterParams, IPaginatedEntityGST<IOcorrenciasByFilterItens[]>>({
    api: 'Gst',
    isPaginated: true,
    params: {
      ...FUNCTIONS.filterObject(methods.getValues(), Boolean),
    },
    url: 'Ocorrencia/GetOcorrencias'
  })

  const handleSubmitForm = (form: IForm) => {
    refreshTable({
      ...FUNCTIONS.filterObject({
        ...form,
        cdTipoFornecedor: form.cdTipoFornecedor && String(form.cdTipoFornecedor['value']),
        nuCompetenciaFim: form.nuCompetenciaFim && moment(form.nuCompetenciaFim).format('YYYYMM'),
        nuCompetenciaInicio: form.nuCompetenciaInicio && moment(form.nuCompetenciaInicio).format('YYYYMM'),
        txCpfCnpjPassaporte: FORMAT.unMask(form.txCpfCnpjPassaporte),
      }, Boolean),
      pageNumber: 1,
    })
  }

  return (
    <FormProvider {...methods}>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <SearchForm
          refreshTable={refreshTable}
          handleSubmitForm={handleSubmitForm}
          isFirstGet={isFirstGet}
        />
        <ErrorBoundary>
          <AccidentTable
            refreshTable={refreshTable}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            tableData={tableData}
          />
        </ErrorBoundary>
      </Card>
    </FormProvider>
  )
}

export default Search
